import { useEffect, useState, useCallback } from "react";
import WindowSearch from "@/components/WindowSearch";
import { toast } from "sonner";
import { Loader2, RefreshCw, PlayCircle, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_STYLE = {
  ready: "bg-[#22c55e]/15 text-[#22c55e]",
  processing: "bg-[#00F0FF]/15 text-[#00F0FF]",
  recovering: "bg-[#a78bfa]/20 text-[#c4b5fd]",
  error: "bg-red-500/15 text-red-400",
  cancelled: "bg-white/10 text-[#a1a1aa]",
};

function Pill({ children, cls }) {
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${cls || "bg-white/5 text-[#a1a1aa]"}`}>{children}</span>;
}

export default function RenderJobs() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");
  const [bench, setBench] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.adminRenderJobs(filter, 60).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => { api.adminRenderBenchmarks().then((d) => setBench((d.benchmarks || [])[0])).catch(() => {}); }, []);

  const resume = async (eid) => {
    setBusy(eid);
    try {
      await api.resumeRenderJob(eid);
      toast.success("Resuming from last checkpoint — completed segments are reused.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume this job.");
    } finally { setBusy(""); }
  };

  const openDetail = (eid) => { setDetail({ id: eid, loading: true }); api.adminRenderJobDetail(eid).then((d) => setDetail(d)).catch(() => setDetail(null)); };

  const rel = data?.reliability || {};
  const counts = data?.counts || {};

  return (
    <div className="rounded-xl border border-white/10 bg-[#0e0f11] p-4" data-testid="render-jobs-panel">
      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm font-bold text-white">
        <PlayCircle size={15} className="text-[#a78bfa]" /> Render Jobs
        <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-semibold text-[#A1A1AA]">checkpoint · resume · live</span>
        <button data-testid="rj-refresh" onClick={load} className="ml-auto flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-[#A1A1AA] hover:text-white">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Refresh
        </button>
      </div>

      {/* Reliability rollups */}
      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
        {[
          ["Total", rel.total_jobs ?? 0, ""],
          ["Completed", rel.completed ?? 0, "text-[#22c55e]"],
          ["Failed", rel.failed ?? 0, "text-red-400"],
          ["Resumed", rel.resumed_jobs ?? 0, "text-[#a78bfa]"],
          ["Failure rate", `${Math.round((rel.failure_rate || 0) * 100)}%`, ""],
          ["Active/Queued", `${(counts.processing || 0) + (counts.recovering || 0)}/${counts.queued || 0}`, "text-[#00F0FF]"],
        ].map(([label, val, cls]) => (
          <div key={label} className="rounded-lg border border-white/5 bg-black/30 p-2 text-center">
            <div className={`text-lg font-bold tabular-nums ${cls || "text-white"}`} data-testid={`rj-stat-${label.toLowerCase().replace(/[^a-z]/g, "")}`}>{val}</div>
            <div className="text-[10px] uppercase tracking-wide text-[#71717a]">{label}</div>
          </div>
        ))}
      </div>
      {rel.most_common_failure && (
        <div className="mb-3 text-[11px] text-[#71717a]">Most common failure: <span className="text-[#a1a1aa]">{rel.most_common_failure}</span></div>
      )}

      {bench && (
        <div className="mb-3 rounded-lg border border-white/5 bg-black/30 p-3" data-testid="rj-benchmark">
          <div className="mb-1 flex items-center gap-2 text-[11px] font-bold text-[#a78bfa]">
            Render Benchmark — {bench.label}
            <span className="rounded bg-white/5 px-1.5 py-0.5 text-[9px] font-semibold text-[#71717a]">
              {bench.env?.encoder} {bench.env?.preset} · {bench.env?.cpu_cores}c · {bench.env?.gpu ? "GPU" : "CPU"}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[11px]">
              <thead className="text-[9px] uppercase tracking-wide text-[#71717a]"><tr>
                <th className="py-1 pr-3">Case</th><th className="py-1 pr-3">Res</th>
                <th className="py-1 pr-3">Before</th><th className="py-1 pr-3">After</th><th className="py-1">Speedup</th>
              </tr></thead>
              <tbody className="tabular-nums text-[#d4d4d8]">
                {(bench.cases || []).map((c, i) => (
                  <tr key={i} data-testid={`rj-bench-row-${i}`}>
                    <td className="py-0.5 pr-3">{c.case}</td><td className="py-0.5 pr-3 text-[#71717a]">{c.res}</td>
                    <td className="py-0.5 pr-3">{c.before_s}s</td><td className="py-0.5 pr-3 text-[#22c55e]">{c.after_s}s</td>
                    <td className="py-0.5 font-bold text-[#00F0FF]">{c.before_s && c.after_s ? `${(c.before_s / c.after_s).toFixed(2)}×` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {bench.note && <div className="mt-1 text-[9px] text-[#555]">{bench.note}</div>}
        </div>
      )}

      <div className="mb-2 flex flex-wrap gap-1.5">
        {["", "processing,recovering", "error", "ready", "cancelled"].map((f) => (
          <button key={f || "all"} data-testid={`rj-filter-${f || "all"}`} onClick={() => setFilter(f)}
            className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors ${filter === f ? "bg-[#a78bfa] text-black" : "border border-white/15 text-[#A1A1AA] hover:text-white"}`}>
            {f === "" ? "All" : f === "processing,recovering" ? "Active" : f[0].toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-lg border border-white/5">
        <table className="w-full text-left text-xs">
          <thead className="bg-white/5 text-[10px] uppercase tracking-wide text-[#71717a]">
            <tr>
              <th className="px-3 py-2">Job</th><th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Segments</th><th className="px-3 py-2">Progress</th>
              <th className="px-3 py-2">Resumes</th><th className="px-3 py-2">Failure</th><th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {(data?.jobs || []).map((j) => (
              <tr key={j.id} className="hover:bg-white/5" data-testid={`rj-row-${j.id}`}>
                <td className="px-3 py-2 font-mono text-[10px] text-[#a1a1aa]">
                  <button className="flex items-center gap-1 hover:text-white" onClick={() => openDetail(j.id)}>
                    {j.id.slice(0, 8)} <ChevronRight size={11} />
                  </button>
                  <div className="text-[9px] text-[#555]">{j.resolution} · {j.encoder || "—"}</div>
                </td>
                <td className="px-3 py-2"><Pill cls={STATUS_STYLE[j.status]}>{j.stale ? "INTERRUPTED" : (j.state || j.status)}</Pill></td>
                <td className="px-3 py-2 tabular-nums text-[#d4d4d8]">{j.completed_segments != null ? `${j.completed_segments}/${j.total_segments ?? "?"}` : "—"}</td>
                <td className="px-3 py-2 tabular-nums">{j.progress ?? 0}%</td>
                <td className="px-3 py-2 tabular-nums text-[#a78bfa]">{j.resume_count || 0}</td>
                <td className="px-3 py-2 text-[10px] text-red-400">{j.error_code || "—"}</td>
                <td className="px-3 py-2">
                  {(j.resumable || j.stale || j.status === "error") && j.status !== "ready" ? (
                    <button data-testid={`rj-resume-${j.id}`} disabled={busy === j.id} onClick={() => resume(j.id)}
                      className="rounded border border-[#a78bfa]/40 px-2 py-1 text-[10px] font-semibold text-[#c4b5fd] hover:bg-[#a78bfa]/10 disabled:opacity-50">
                      {busy === j.id ? "…" : "Resume"}
                    </button>
                  ) : "—"}
                </td>
              </tr>
            ))}
            {!(data?.jobs || []).length && <tr><td colSpan={7} className="px-3 py-4 text-center text-[#71717a]">No render jobs.</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setDetail(null)}>
          <div className="relative max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-xl border border-white/10 bg-[#0e0f11] p-5" onClick={(e) => e.stopPropagation()} data-testid="rj-detail-modal">
            <WindowSearch />
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-bold text-white">Render Job</h3>
              <button onClick={() => setDetail(null)} className="text-[#71717a] hover:text-white">✕</button>
            </div>
            {detail.loading ? <Loader2 className="mx-auto animate-spin text-[#a78bfa]" /> : (
              <div className="space-y-2 text-xs text-[#d4d4d8]">
                <div className="font-mono text-[10px] text-[#71717a]">{detail.id}</div>
                {[["Project", detail.project_title], ["Status", `${detail.state || detail.status} (${detail.progress ?? 0}%)`],
                  ["Segments", `${detail.completed_segments ?? "—"} / ${detail.total_segments ?? "?"}`],
                  ["Finalization", detail.finalization_status], ["Encoder", detail.encoder], ["Resolution", detail.resolution],
                  ["Duration", detail.duration ? `${detail.duration}s` : "—"], ["Resume count", detail.resume_count],
                  ["Failure stage", detail.error_code || "—"], ["Failure detail", detail.error_message || "—"],
                  ["Worker PID", detail.worker_pid], ["Last heartbeat", detail.last_heartbeat]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-white/5 pb-1"><span className="text-[#71717a]">{k}</span><span className="text-right">{String(v ?? "—")}</span></div>
                ))}
                {detail.segments?.length ? (
                  <div className="pt-2">
                    <div className="mb-1 text-[10px] uppercase tracking-wide text-[#71717a]">Segment checkpoints ({detail.segments.length})</div>
                    <div className="flex flex-wrap gap-1">
                      {detail.segments.map((s, i) => (
                        <span key={i} title={`${s.s}s–${s.e}s${s.cached ? " (cached)" : ""}`} className={`h-4 w-4 rounded-sm ${s.cached ? "bg-[#a78bfa]" : "bg-[#22c55e]"}`} />
                      ))}
                    </div>
                    <div className="mt-1 text-[9px] text-[#555]">green = rendered · purple = reused from cache (resume)</div>
                  </div>
                ) : null}
                {(detail.resumable || detail.status === "error") && detail.status !== "ready" && (
                  <button data-testid="rj-detail-resume" onClick={() => { resume(detail.id); setDetail(null); }}
                    className="mt-2 w-full rounded-lg border border-[#a78bfa]/40 py-2 font-semibold text-[#c4b5fd] hover:bg-[#a78bfa]/10">Resume from last checkpoint</button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
